import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { logger } from "@/lib/logger";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  Globe2, Facebook, Instagram, Linkedin, Youtube, Music2, BookImage,
  Twitter, Search, BarChart3, MapPin, MessageSquare, Star, Mail,
  Sparkles, Bot, ChevronDown, ChevronRight, Network, ExternalLink, Loader2,
  ThumbsUp, Building2, Smartphone,
} from "lucide-react";

// Per-channel icon + accent colour. Falls back to a generic icon.
const CHANNEL_META = {
  facebook:           { Icon: Facebook,      accent: "#1877F2" },
  instagram:          { Icon: Instagram,     accent: "#E4405F" },
  linkedin:           { Icon: Linkedin,      accent: "#0A66C2" },
  youtube:            { Icon: Youtube,       accent: "#FF0000" },
  tiktok:             { Icon: Music2,        accent: "#000000" },
  pinterest:          { Icon: BookImage,     accent: "#E60023" },
  x_twitter:          { Icon: Twitter,       accent: "#000000" },
  google_ads:         { Icon: Search,        accent: "#4285F4" },
  google_search:      { Icon: Search,        accent: "#34A853" },
  bing_search:        { Icon: Search,        accent: "#008373" },
  bing_ads:           { Icon: BarChart3,     accent: "#008373" },
  gbp:                { Icon: MapPin,        accent: "#4285F4" },
  bing_business:      { Icon: Building2,     accent: "#008373" },
  google_reviews:     { Icon: Star,          accent: "#4285F4" },
  facebook_reviews:   { Icon: ThumbsUp,      accent: "#1877F2" },
  productreview:      { Icon: MessageSquare, accent: "#EE7C00" },
  trustpilot:         { Icon: Star,          accent: "#00B67A" },
  email:              { Icon: Mail,          accent: "#1A82E2" },
  sms_rcs:            { Icon: Smartphone,    accent: "#7E22CE" },
  chatgpt:            { Icon: Sparkles,      accent: "#10A37F" },
  microsoft_copilot:  { Icon: Bot,           accent: "#0078D4" },
  gemini:             { Icon: Sparkles,      accent: "#4285F4" },
};

const CATEGORY_LABELS = {
  social:   "Social channels",
  search:   "Search & ads",
  listings: "Local listings",
  reviews:  "Reviews",
  direct:   "Direct",
  ai:       "AI search & discovery",
};
const CATEGORY_ORDER = ["social", "search", "listings", "reviews", "direct", "ai"];

export default function EcosystemDiagram({ clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openIdx, setOpenIdx] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${clientId}/ecosystem`);
      setData(data);
    } catch (e) {
      // non-fatal — the Overview page still renders without the diagram.
      logger.warn("EcosystemDiagram load failed", e);
    } finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-6 text-sm text-zinc-500 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading ecosystem…
        </CardContent>
      </Card>
    );
  }
  if (!data || !data.properties?.length) {
    return null;
  }

  const present = Object.values(data.channels || {}).filter(Boolean).length;
  const total = (data.catalogue || []).length;

  return (
    <Card className="rounded-sm border-zinc-200" data-testid="ecosystem-diagram">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
          <div>
            <div className="label-overline flex items-center gap-1">
              <Network className="h-3 w-3" /> Marketing ecosystem
            </div>
            <h2 className="font-display font-bold text-2xl tracking-tight mt-1">
              Your website is the centre of everything
            </h2>
            <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
              Each property below is the hub for its own marketing ecosystem. Green tiles are channels
              we&apos;re actively measuring — grey tiles are ones we recommend turning on next.
            </p>
          </div>
          <Badge variant="outline" className="rounded-sm font-mono text-[11px]">
            {present}/{total} channels active
          </Badge>
        </div>

        <div className="space-y-3 mt-4">
          {data.properties.map((p, i) => (
            <PropertyAccordion key={p.business_unit_id || i}
              property={p}
              open={openIdx === i}
              onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
              catalogue={data.catalogue}
              channels={data.channels} />
          ))}
        </div>

        <TikTokMiniChart clientId={clientId} />
      </CardContent>
    </Card>
  );
}

function TikTokMiniChart({ clientId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/clients/${clientId}/tiktok/history?days=90`);
        if (!cancelled) setRows(data?.rows || []);
      } catch (e) {
        logger.debug("tiktok mini chart skipped", e?.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  if (loading || rows.length < 2) return null;

  const data = rows.map((r) => ({
    date: (r.date || "").slice(5),
    views: Number((r.metrics || {}).video_views || 0),
    followers: Number((r.metrics || {}).followers || 0),
  }));
  const hasViews = data.some((d) => d.views > 0);
  const hasFollowers = data.some((d) => d.followers > 0);
  if (!hasViews && !hasFollowers) return null;

  return (
    <div className="mt-5 pt-5 border-t border-zinc-100" data-testid="ecosystem-tiktok-chart">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="label-overline flex items-center gap-1.5">
          <Music2 className="h-3 w-3" /> TikTok · last 90 days
        </div>
        <div className="text-[10px] font-mono text-zinc-400">
          {rows.length} day{rows.length === 1 ? "" : "s"} of history
        </div>
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <LineChart data={data} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
          <YAxis tick={{ fontSize: 10 }} />
          <Tooltip wrapperStyle={{ fontSize: 11 }} />
          {hasViews && (
            <Line type="monotone" dataKey="views" stroke="#FF0050"
              strokeWidth={2} dot={false} name="Video views" />
          )}
          {hasFollowers && (
            <Line type="monotone" dataKey="followers" stroke="#00F2EA"
              strokeWidth={2} dot={false} name="Followers" />
          )}
        </LineChart>
      </ResponsiveContainer>
      <div className="flex items-center gap-3 flex-wrap text-[10px] mt-1 text-zinc-600">
        {hasViews && (
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-2 w-3 rounded-sm bg-[#FF0050]" /> Video views
          </span>
        )}
        {hasFollowers && (
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-2 w-3 rounded-sm bg-[#00F2EA]" /> Followers
          </span>
        )}
      </div>
    </div>
  );
}

function PropertyAccordion({ property, open, onToggle, catalogue, channels }) {
  return (
    <div className="rounded-sm border border-zinc-200 overflow-hidden">
      <button type="button"
        className={`w-full px-4 py-3 flex items-center justify-between gap-3 transition-colors ${open ? "bg-zinc-50" : "hover:bg-zinc-50/60"}`}
        onClick={onToggle}
        data-testid={`ecosystem-toggle-${property.business_unit_id}`}>
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-9 w-9 rounded-sm bg-[#002FA7] text-white grid place-items-center shrink-0">
            <Globe2 className="h-4 w-4" />
          </div>
          <div className="text-left min-w-0">
            <div className="font-display font-bold text-sm truncate">{property.name}</div>
            {property.url && (
              <div className="text-[11px] font-mono text-zinc-500 truncate inline-flex items-center gap-1">
                {property.url}
                <ExternalLink className="h-3 w-3" />
              </div>
            )}
          </div>
        </div>
        {open ? <ChevronDown className="h-4 w-4 text-zinc-400" /> : <ChevronRight className="h-4 w-4 text-zinc-400" />}
      </button>

      {open && (
        <div className="px-4 py-5 sm:px-6 sm:py-6 border-t border-zinc-100 bg-gradient-to-br from-zinc-50/40 to-white">
          <Diagram property={property} catalogue={catalogue} channels={channels} />
        </div>
      )}
    </div>
  );
}

function Diagram({ property, catalogue, channels }) {
  // Group catalogue by category and split present/missing.
  const byCategory = {};
  for (const c of catalogue) {
    (byCategory[c.category] = byCategory[c.category] || []).push(c);
  }
  const activeCategories = CATEGORY_ORDER.filter((c) => byCategory[c]);
  // Distribute categories evenly left/right of the central hub so the
  // diagram reads as a true hub-and-spoke: the website sits in the
  // middle, channels radiate outward.
  const mid = Math.ceil(activeCategories.length / 2);
  const leftCats = activeCategories.slice(0, mid);
  const rightCats = activeCategories.slice(mid);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-4 lg:gap-6 items-center">
        {/* LEFT column of category groups */}
        <div className="space-y-4 order-2 lg:order-1">
          {leftCats.map((cat) => (
            <CategoryBlock key={cat} cat={cat}
              items={byCategory[cat]} channels={channels} align="right" />
          ))}
        </div>

        {/* CENTRE hub: the property/website itself */}
        <div className="flex flex-col items-center text-center order-1 lg:order-2 relative py-2">
          {/* Decorative connector lines, hidden on small screens. */}
          <div aria-hidden className="hidden lg:block absolute inset-y-1/2 left-[-32px] right-[-32px] h-px bg-zinc-200 -z-10" />
          <div aria-hidden className="hidden lg:block absolute inset-x-1/2 top-[10%] bottom-[10%] w-px bg-zinc-200 -z-10" />
          <div className="h-24 w-24 rounded-sm bg-[#002FA7] text-white grid place-items-center shadow-lg ring-4 ring-[#002FA7]/15 relative z-10">
            <Globe2 className="h-10 w-10" />
          </div>
          <div className="mt-3 font-display font-extrabold text-lg">
            {property.name}
          </div>
          {property.url && (
            <a href={property.url.startsWith("http") ? property.url : `https://${property.url}`}
              target="_blank" rel="noreferrer"
              className="text-[11px] font-mono text-[#002FA7] hover:underline">
              {property.url} <ExternalLink className="inline h-3 w-3" />
            </a>
          )}
        </div>

        {/* RIGHT column of category groups */}
        <div className="space-y-4 order-3">
          {rightCats.map((cat) => (
            <CategoryBlock key={cat} cat={cat}
              items={byCategory[cat]} channels={channels} align="left" />
          ))}
        </div>
      </div>

      <p className="text-[11px] font-mono text-zinc-400 text-center pt-2 border-t border-zinc-100">
        Channels join the hub when we detect them in the data (CSVs, OAuth connections, webhooks, or assignments).
      </p>
    </div>
  );
}

function CategoryBlock({ cat, items, channels, align = "left" }) {
  return (
    <div>
      <div className={`label-overline mb-2 ${align === "right" ? "text-right lg:text-right" : ""}`}>
        {CATEGORY_LABELS[cat]}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {items.map((ch) => (
          <ChannelTile key={ch.key} channel={ch}
            present={!!channels[ch.key]} />
        ))}
      </div>
    </div>
  );
}

function ChannelTile({ channel, present }) {
  const meta = CHANNEL_META[channel.key] || { Icon: Sparkles, accent: "#71717A" };
  const Icon = meta.Icon;

  return (
    <div className={`rounded-sm border px-2.5 py-2 flex items-center gap-2 transition-colors ${
      present
        ? "border-emerald-200 bg-emerald-50/70"
        : "border-dashed border-zinc-300 bg-white text-zinc-400"
    }`}
      data-testid={`ecosystem-channel-${channel.key}`}
      title={present ? `${channel.label} — active` : `${channel.label} — not yet set up`}>
      <div className="h-6 w-6 rounded-sm grid place-items-center shrink-0"
        style={present ? { backgroundColor: meta.accent, color: "white" } : { backgroundColor: "#F4F4F5" }}>
        <Icon className="h-3.5 w-3.5" strokeWidth={2} />
      </div>
      <div className="min-w-0 flex-1">
        <div className={`text-[12px] font-semibold truncate ${present ? "text-emerald-900" : "text-zinc-500"}`}>
          {channel.label}
        </div>
        <div className={`text-[9px] font-mono uppercase tracking-wider ${present ? "text-emerald-700" : "text-zinc-400"}`}>
          {present ? "active" : "not set up"}
        </div>
      </div>
    </div>
  );
}
